import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminApi } from '../lib/adminApi';
import {
  LogOut,
  FileText,
  Briefcase,
  Image as ImageIcon,
  Video,
  Home,
  Users,
  Calendar,
  BookOpen,
  BedDouble,
} from 'lucide-react';
import ProjectsManager from '../components/admin/ProjectsManager';
import GalleryManager from '../components/admin/GalleryManager';
import EventsManager from '../components/admin/EventsManager';
import VideosManager from '../components/admin/VideosManager';
import AboutManager from '../components/admin/AboutManager';
import AboutModalManager from '../components/admin/AboutModalManager';
import CareersManager from '../components/admin/CareersManager';
import RoomBookingManager from '../components/admin/RoomBookingManager';
import AuditLogViewer from '../components/admin/AuditLogViewer';
import BranchesManager from '../components/admin/BranchesManager';
import GenericArticleManager from '../components/admin/GenericArticleManager';
import DonationQRManager from '../components/admin/DonationQRManager';
import PartnersManager from '../components/admin/PartnersManager';
import AboutSubsectionManager from '../components/admin/AboutSubsectionManager';
import BannerManager from '../components/admin/BannerManager';
import CountdownManager from '../components/admin/CountdownManager';
import FooterManager from '../components/admin/FooterManager';
import HeroContentManager from '../components/admin/HeroContentManager';
import HeroSlidesManager from '../components/admin/HeroSlidesManager';
import TranslationsManager from '../components/admin/TranslationsManager';
import SiteLockManager from '../components/admin/SiteLockManager';
import BackupManager from '../components/admin/BackupManager';
import CredentialsManager from '../components/admin/CredentialsManager';
import EnquiriesManager from '../components/admin/EnquiriesManager';
import SiteMonitor from '../components/admin/SiteMonitor';
import AnnualReportsManager from '../components/admin/AnnualReportsManager';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [stats, setStats] = useState({ projects: 0, events: 0, gallery: 0, videos: 0, careers: 0 });

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin');
        return;
      }
      try {
        await adminApi.get('/verify');
        setUsername(localStorage.getItem('admin_username') || 'admin');
        await fetchStats();
        setLoading(false);
      } catch {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const [p, e, g, v, c] = await Promise.all([
        adminApi.get('/projects'),
        adminApi.get('/events'),
        adminApi.get('/gallery'),
        adminApi.get('/videos'),
        adminApi.get('/careers'),
      ]);
      setStats({
        projects: p.data.length,
        events: e.data.length,
        gallery: g.data.length,
        videos: v.data.length,
        careers: c.data.length,
      });
    } catch (err) {
      console.error('stats', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1565C0] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="bg-gradient-to-r from-[#1976D2] to-[#1565C0] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_team-sync-95/artifacts/3qbgpcfe_WhatsApp%20Image%202026-05-19%20at%204.29.59%20PM.jpeg"
                alt="EWRF Logo"
                className="h-12 rounded bg-white p-1"
              />
              <div>
                <h1 className="text-xl md:text-2xl font-bold">EWRF Admin Dashboard</h1>
                <p className="text-sm text-white/80">Welcome, {username}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => window.open('/', '_blank')}
                variant="outline"
                className="border-white text-white hover:bg-white/10 bg-transparent"
                data-testid="view-website-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                View Website
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white text-white hover:bg-white/10 bg-transparent"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="site-monitor" className="space-y-6">
          <TabsList className="flex flex-wrap gap-1 h-auto justify-start">
            <TabsTrigger value="site-monitor" data-testid="tab-site-monitor">Site Monitor</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
            <TabsTrigger value="about-modal" data-testid="tab-about-modal">About Modal</TabsTrigger>
            <TabsTrigger value="history-stages" data-testid="tab-history-stages">History Stages</TabsTrigger>
            <TabsTrigger value="committee" data-testid="tab-committee">Committee</TabsTrigger>
            <TabsTrigger value="past-presidents" data-testid="tab-past-presidents">Past Presidents</TabsTrigger>
            <TabsTrigger value="our-team" data-testid="tab-our-team">Our Team</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="hq-events" data-testid="tab-hq-events">HQ Events</TabsTrigger>
            <TabsTrigger value="branch-activities" data-testid="tab-branch-activities">Branch Activities</TabsTrigger>
            <TabsTrigger value="media-coverage" data-testid="tab-media-coverage">Media Coverage</TabsTrigger>
            <TabsTrigger value="media-statements" data-testid="tab-media-statements">News Releases</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">News Letter</TabsTrigger>
            <TabsTrigger value="research-papers" data-testid="tab-research-papers">Research Papers</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="careers" data-testid="tab-careers">Careers</TabsTrigger>
            <TabsTrigger value="room" data-testid="tab-room">Room Booking</TabsTrigger>
            <TabsTrigger value="branches" data-testid="tab-branches">Branches</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Supporters</TabsTrigger>
            <TabsTrigger value="donation-qr" data-testid="tab-donation-qr">Donation QR</TabsTrigger>
            <TabsTrigger value="banner" data-testid="tab-banner">Banner</TabsTrigger>
            <TabsTrigger value="countdown" data-testid="tab-countdown">Countdown</TabsTrigger>
            <TabsTrigger value="footer" data-testid="tab-footer">Footer</TabsTrigger>
            <TabsTrigger value="hero" data-testid="tab-hero">Hero Text</TabsTrigger>
            <TabsTrigger value="hero-slides" data-testid="tab-hero-slides">Hero Slider</TabsTrigger>
            <TabsTrigger value="translations" data-testid="tab-translations">Translations</TabsTrigger>
            <TabsTrigger value="site-lock" data-testid="tab-site-lock">Site Lock</TabsTrigger>
            <TabsTrigger value="annual-reports" data-testid="tab-annual-reports">Annual Reports</TabsTrigger>
            <TabsTrigger value="credentials" data-testid="tab-credentials">Trust Badges</TabsTrigger>
            <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="site-monitor"><SiteMonitor /></TabsContent>

          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Projects', value: stats.projects, icon: Briefcase, color: 'from-blue-500 to-blue-700' },
                { label: 'Events', value: stats.events, icon: Calendar, color: 'from-emerald-500 to-emerald-700' },
                { label: 'Gallery', value: stats.gallery, icon: ImageIcon, color: 'from-purple-500 to-purple-700' },
                { label: 'Videos', value: stats.videos, icon: Video, color: 'from-orange-500 to-orange-700' },
                { label: 'Careers', value: stats.careers, icon: Users, color: 'from-rose-500 to-rose-700' },
              ].map((s) => (
                <Card key={s.label} className={`bg-gradient-to-br ${s.color} text-white`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <s.icon className="w-5 h-5" />
                      {s.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold" data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="mt-6">
              <CardHeader><CardTitle>Quick Guide</CardTitle></CardHeader>
              <CardContent className="text-sm text-gray-600 space-y-2">
                <p>• Use the tabs above to manage content. Changes go live immediately on the public site.</p>
                <p>• Drag-and-drop images directly into upload zones to add media.</p>
                <p>• Drag rows in lists (Projects, Events, Videos, Careers) to reorder them on the site.</p>
              </CardContent>
            </Card>

            <div className="mt-6">
              <AuditLogViewer compact limit={10} />
            </div>
          </TabsContent>

          <TabsContent value="about"><AboutManager /></TabsContent>
          <TabsContent value="about-modal"><AboutModalManager /></TabsContent>
          <TabsContent value="history-stages">
            <AboutSubsectionManager field="history_phases" title="History Stages" variant="history" />
          </TabsContent>
          <TabsContent value="committee">
            <AboutSubsectionManager field="committee_members" title="Committee Members" variant="people" />
          </TabsContent>
          <TabsContent value="past-presidents">
            <AboutSubsectionManager field="past_presidents_list" title="Past Presidents" variant="people" />
          </TabsContent>
          <TabsContent value="our-team">
            <AboutSubsectionManager field="staff_members" title="Our Team" variant="people" />
          </TabsContent>
          <TabsContent value="projects"><ProjectsManager /></TabsContent>
          <TabsContent value="events"><EventsManager /></TabsContent>
          <TabsContent value="hq-events"><GenericArticleManager slug="hq-events" collectionName="hq_events" title="HQ Events" /></TabsContent>
          <TabsContent value="branch-activities"><GenericArticleManager slug="branch-activities" collectionName="branch_activities" title="Branch Activities" /></TabsContent>
          <TabsContent value="media-coverage"><GenericArticleManager slug="media-coverage" collectionName="media_coverage" title="Media Coverage" showSource /></TabsContent>
          <TabsContent value="media-statements"><GenericArticleManager slug="media-statements" collectionName="media_statements" title="News Releases" /></TabsContent>
          <TabsContent value="newsletter"><GenericArticleManager slug="newsletter" collectionName="newsletter" title="News Letter" pdfMode /></TabsContent>
          <TabsContent value="research-papers"><GenericArticleManager slug="research-papers" collectionName="research_papers" title="Research Papers" pdfMode /></TabsContent>
          <TabsContent value="gallery"><GalleryManager /></TabsContent>
          <TabsContent value="videos"><VideosManager /></TabsContent>
          <TabsContent value="careers"><CareersManager /></TabsContent>
          <TabsContent value="room"><RoomBookingManager /></TabsContent>
          <TabsContent value="branches"><BranchesManager /></TabsContent>
          <TabsContent value="partners"><PartnersManager /></TabsContent>
          <TabsContent value="donation-qr"><DonationQRManager /></TabsContent>
          <TabsContent value="banner"><BannerManager /></TabsContent>
          <TabsContent value="countdown"><CountdownManager /></TabsContent>
          <TabsContent value="footer"><FooterManager /></TabsContent>
          <TabsContent value="hero"><HeroContentManager /></TabsContent>
          <TabsContent value="hero-slides"><HeroSlidesManager /></TabsContent>
          <TabsContent value="translations"><TranslationsManager /></TabsContent>
          <TabsContent value="site-lock"><SiteLockManager /></TabsContent>
          <TabsContent value="annual-reports"><AnnualReportsManager /></TabsContent>
          <TabsContent value="credentials"><CredentialsManager /></TabsContent>
          <TabsContent value="backup"><BackupManager /></TabsContent>
          <TabsContent value="audit"><AuditLogViewer /></TabsContent>
          <TabsContent value="enquiries"><EnquiriesManager /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
